import { StyledLink } from '@/components/escript/onboarding/Onboarding.styles';
import { usePreOnboardingContext } from '@/hooks/escript/usePreOnboardingContext';
import { paymentMethods } from '@/pages/escript/landing/PaymentMethods';
import { LOGIN_ROUTE, UMEDS_MARKETING } from '@/routes/route-paths';
import { ContainedButtonInverted, OutlinedButtonInverted } from '@/theme/buttons';
import UmedsLogo from '@montugroup/circuit-shared/components/common/UmedsLogo';
import { Box, Link, Stack, Typography } from '@montugroup/design-system';
import { MgFacebookAlt, MgInstagramAlt, MgLinkedInAlt } from '@montugroup/icons';
import { Grid2, styled } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import {
  SocialIcons,
  StyledNav,
  StyledPaymentOptionsFooter,
  StyledUmedsLogoFooterSubText,
} from './OnboardingFooter.styles';

const StyledUmedsLogoFooter = styled(UmedsLogo)(() => ({
  width: '164px',
}));

const StyledUmedsLogoFooterSubTextMobile = styled(StyledUmedsLogoFooterSubText)(({ theme }) => ({
  fontSize: '1rem',
  marginTop: theme.spacing(3.5),
}));

const StyledStack = styled(Stack)(({ theme }) => ({
  marginBottom: theme.spacing(10),
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
}));

export function OnboardingFooterMobile() {
  const { resetOnboardingState } = usePreOnboardingContext();

  return (
    <Grid2 container spacing={2}>
      <Grid2 size={{ xs: 12 }} sx={{ alignItems: 'center' }}>
        <Box>
          <StyledStack>
            <StyledUmedsLogoFooter fillColor={(theme) => theme.palette.secondary.main}></StyledUmedsLogoFooter>
            <StyledUmedsLogoFooterSubTextMobile>In good hands</StyledUmedsLogoFooterSubTextMobile>
          </StyledStack>

          <StyledStack gap={6}>
            <ContainedButtonInverted
              size="large"
              variant="contained"
              onClick={resetOnboardingState}
              fullWidth
              data-tracking-id="escript_onboarding_footer_prescription"
            >
              Submit Prescription
            </ContainedButtonInverted>

            <OutlinedButtonInverted
              size="large"
              variant="outlined"
              component={RouterLink}
              to={LOGIN_ROUTE}
              color="primary"
              fullWidth
              data-tracking-id="escript_onboarding_footer_login"
            >
              Sign In
            </OutlinedButtonInverted>
          </StyledStack>

          <StyledNav component="nav" aria-label="Footer Navigation" sx={{ justifyContent: 'center', marginBottom: 8 }}>
            <SocialIcons>
              <Link
                href="https://www.facebook.com/profile.php?id=61557205078310"
                data-tracking-id="escript_onboarding_footer_facebook"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook"
              >
                <MgFacebookAlt />
              </Link>

              <Link
                href="http://linkedin.com/company/umeds-australia"
                data-tracking-id="escript_onboarding_footer_linkedin"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="LinkedIn"
              >
                <MgLinkedInAlt />
              </Link>

              <Link
                href="https://www.instagram.com/umedsau/"
                data-tracking-id="escript_onboarding_footer_instagram"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
              >
                <MgInstagramAlt />
              </Link>
            </SocialIcons>
          </StyledNav>

          <Box>
            <Typography
              variant="body2"
              sx={(theme) => ({
                marginBottom: theme.spacing(4),
                color: theme.palette.common.white,
                textAlign: 'center',
              })}
            >
              &copy; {new Date().getFullYear()} uMeds Australia Pty Ltd. All rights reserved.
            </Typography>

            <Grid2 container sx={(theme) => ({ marginBottom: theme.spacing(6), justifyContent: 'center' })}>
              <Box
                sx={(theme) => ({
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, auto)',
                  gap: theme.spacing(4),
                })}
              >
                {paymentMethods.map((item, index) => (
                  <StyledPaymentOptionsFooter key={`${item.name}-${index}-mobile`}>
                    <img src={item.src} alt={item.name} title={item.name} />
                  </StyledPaymentOptionsFooter>
                ))}
              </Box>
            </Grid2>
          </Box>
          <Stack direction="row" spacing={8} sx={{ justifyContent: 'center' }}>
            <StyledLink
              href={UMEDS_MARKETING.PRIVACY_POLICY}
              data-tracking-id="escript_onboarding_footer_privacy_policy"
            >
              <Typography variant="body2" sx={(theme) => ({ color: theme.palette.common.white })}>
                Privacy Policy
              </Typography>
            </StyledLink>

            <StyledLink
              href={UMEDS_MARKETING.TERMS_CONDITIONS}
              data-tracking-id="escript_onboarding_footer_terms_conditions"
            >
              <Typography variant="body2" sx={(theme) => ({ color: theme.palette.common.white })}>
                Terms of Service
              </Typography>
            </StyledLink>
          </Stack>
        </Box>
      </Grid2>
    </Grid2>
  );
}
